import HeroScene from "@/components/HeroScene";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { useTheme } from "@/lib/theme";

export default function Hero() {
  const { theme } = useTheme();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="section-home"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* 3D Canvas */}
      <div className="absolute inset-0 z-0">
        <HeroScene theme={theme} />
      </div>


      {/* Vignette overlay for text readability */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            theme === "light"
              ? "radial-gradient(ellipse at 20% 60%, rgba(244,239,230,0.0) 0%, rgba(244,239,230,0.55) 60%, rgba(244,239,230,0.85) 100%)"
              : "radial-gradient(ellipse at 20% 60%, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      {/* Top corner status chip */}
      <div className="absolute top-28 right-5 sm:right-10 z-10 hidden md:flex items-center gap-2">
        <span className="chip"><span className="dot" /> Available for projects</span>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 min-h-screen flex flex-col justify-end pb-24 pt-32">
        <div className="mb-4 flex items-center gap-3">
          <Sparkles className="w-3.5 h-3.5 text-[var(--lime)]" />
          <span className="overline">KS DevArc — Development & Architecture</span>
        </div>

        <h1
          data-testid="hero-headline"
          className="font-display font-black text-[12vw] md:text-[8.5vw] lg:text-[7.5rem] leading-[0.92] tracking-[-0.03em] max-w-6xl"
        >
          Build<br />
          <span className="inline-flex items-baseline gap-4">
            Smarter
            <span className="hidden md:inline-block w-3 h-3 rounded-full bg-[var(--vermilion)] glow-vermilion" />
          </span><br />
          <span className="text-zinc-400">Digital</span>{" "}
          <span className="italic font-normal">Solutions.</span>
        </h1>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <p className="md:col-span-5 text-[var(--text-1)] font-medium text-base md:text-lg leading-relaxed max-w-md">
            <span className="text-[var(--vermilion)] font-semibold">KS DevArc</span> helps small businesses modernize operations through
            websites, applications, automation, and AI-powered technology
            solutions — built for growth and efficiency.
          </p>

          <div className="md:col-span-4 md:col-start-9 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center gap-3 px-6 py-4 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] text-white text-sm tracking-wide font-medium rounded-full transition-all duration-300 hover:scale-[1.02] glow-vermilion"
            >
              Contact us
              <ArrowDownRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollTo("services")}
              className="inline-flex items-center gap-2 px-5 py-4 border border-[var(--border-dim)] hover:border-[var(--lime)] hover:text-[var(--lime)] text-sm tracking-wide rounded-full transition-colors"
            >
              Explore services
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="mt-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-px w-12 bg-white/20" />
            <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--text-3)]">SCROLL_TO_EXPLORE</span>
          </div>
          <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--text-3)] hidden md:block">
            v.06 / 2026
          </div>
        </div>
      </div>
    </section>
  );
}
